import pushEvent from '@bfe/by-gtm';
import { EToastType, toast } from '@bfe/moly';
import { debounce } from 'lodash-es';
import { useEffect, useState } from 'react';

import SecurityComponent from '@/components/SecurityComponent';
import { useFingerPrint } from '@/hooks/useFingerPrint';
import { useI18nContext } from '@/i18n/I18n';
import { openCaptchaV2 } from '@/safe/components/ByCaptcha/openCaptchaV2';
import { childLogin } from '@/services/user';
import { DEFAULT_CAPTCHA_CONFIG } from '@/utils/constants/common';
import { SUB_ACCOUNT_LOGIN_CAPTCHA_SCENE } from '@/utils/constants/sub-account';
import { getGuid } from '@/utils/guid';

import Google2faModal from '../Google2faModal';
import IsLogin from '../IsLogin';
import { useStore } from '../store/captchaProvider';
import FormContent from './FormContent';
import { formatParams, loginCatch } from './helper';

const LoginForm = ({
  loginSuccess,
  onForbiddenLogin,
  onErrorLimit,
}: {
  loginSuccess: (data: any) => void;
  onForbiddenLogin: (errorCode: number) => void;
  onErrorLimit: () => void;
}) => {
  const [$t] = useI18nContext();
  const { newCallVerifyComp, cookieConfig, loadCaptcha } = useStore();

  const { fingerReport } = useFingerPrint();

  const [showIpCheck, setShowIpCheck] = useState(false);
  const [captchaV2Loading, setCapthaV2Loading] = useState(false);
  const [captchaDataInfo, setCaptchaDataInfo] = useState<any>();
  const [showSecurityComponent, setShowSecurityComponent] = useState('');
  const [loginInfo, setLoginInfo] = useState({
    loginName: '',
    password: '',
  });
  const [show2faModal, setShow2faModal] = useState(false);
  const [bizToken, setBizToken] = useState('');
  const [riskTokenType, setRiskTokenType] = useState('');
  const [userInfo, setUserInfo] = useState(null);

  // 失败次数提示
  const errorTimeToast = (errorCode: number, time: string) => {
    // toast 密码输入错误，您还有{number}次机会!
    const msg = $t('loginErrCloseToLimit').replace('{number}', time);
    toast({
      iconType: EToastType.FAIL,
      title: `${errorCode}: ${msg}`,
    });
  };

  // 通用错误提示
  const errorToast = (params: {
    errorCode: number;
    title?: string;
    ns?: string;
  }) => {
    const { errorCode, title, ns } = params;
    const msg = ns ? $t(title || `${errorCode}`, { ns }) : title;
    toast({
      iconType: EToastType.FAIL,
      title: `${errorCode}: ${msg}`,
    });
  };

  // 帐密输入错误次数太多弹窗提示
  const limitModal = () => {
    onErrorLimit();
  };

  // 安全组件不可用时，兜底需要2fa认证
  const showGoogleCaptchaVerify = (bizToken: string) => {
    setBizToken(bizToken);
    setShow2faModal(true);
  };

  // 行为验证通过后，执行登录操作
  const SecurityComponentSuccess = (
    captchaData = captchaDataInfo,
    risk_token = '',
    google2faCode = '',
  ) => {
    pushEvent('click', 'subaccount_click_login', 'evetLabel');

    if (risk_token === '') {
      setCaptchaDataInfo(captchaData);
    }

    const params = formatParams({
      captchaData,
      loginInfo,
      risk_token,
      google2faCode,
      bizToken,
    });

    childLogin(params)
      .then((data: any) => {
        const { userInfoResult } = data?.result || {};
        if (
          risk_token ||
          google2faCode ||
          userInfoResult?.risk_token_type === 'pass' ||
          (userInfoResult?.risk_token_type === '' &&
            userInfoResult?.risk_token === '')
        ) {
          loginSuccess({
            ...(data.result || {}),
            currentSite: data?.currentSite,
          });
          return false;
        }
        if (userInfoResult?.risk_token !== '') {
          if (userInfoResult?.risk_token_type === 'guide') {
            setUserInfo({
              ...(data.result || {}),
              currentSite: data?.currentSite,
            });
          }
          setBizToken(data?.ext_code);
          setShowSecurityComponent(userInfoResult?.risk_token);
          setRiskTokenType(userInfoResult?.risk_token_type);
        }
      })
      .catch((err) => {
        loginCatch({
          err,
          forbiddenLogin: onForbiddenLogin,
          errorTimeToast,
          errorToast,
          limitModal,
          need2faVerify: showGoogleCaptchaVerify,
        });
      });
  };

  // 行为验证成功回调
  const onCaptchaSuccess = (captchaData: any) => {
    SecurityComponentSuccess(captchaData);
  };

  // 唤起旧版行为验证
  const oldDispatchCaptcha = () => {
    newCallVerifyComp(onCaptchaSuccess);
  };

  // IP验证通过后，执行行为验证
  const login = () => {
    const {
      completelyUseMagpieCatcha = false,
      useDowngrade = true,
      guidTailList = [],
    } = cookieConfig[SUB_ACCOUNT_LOGIN_CAPTCHA_SCENE] ||
    DEFAULT_CAPTCHA_CONFIG[SUB_ACCOUNT_LOGIN_CAPTCHA_SCENE];

    const guid = getGuid();
    const downgradeFn = () => {
      if (useDowngrade) {
        oldDispatchCaptcha();
      }
      setCapthaV2Loading(false);
    };
    if (
      completelyUseMagpieCatcha ||
      guidTailList.some((tail: string) => guid.endsWith(tail))
    ) {
      openCaptchaV2(
        {
          useDowngrade,
          captchaCallBack: onCaptchaSuccess,
          scene: SUB_ACCOUNT_LOGIN_CAPTCHA_SCENE,
          business_type: 'subAccount',
        },
        downgradeFn,
        setCapthaV2Loading,
      );
    } else {
      oldDispatchCaptcha();
    }
  };

  // 点击登录按钮
  // 上报指纹，进行 IP 验证
  const handleLogin = debounce((params) => {
    fingerReport('wf03', '6');
    setLoginInfo(params);
    setShowIpCheck(true); // IP验证组件
  }, 500);

  useEffect(() => {
    loadCaptcha();
  }, []);

  return (
    <>
      {/* 登录表单 */}
      <FormContent
        captchaV2Loading={captchaV2Loading}
        onHandleLogin={handleLogin}
      />
      {/* IP 验证组件 */}
      {showIpCheck && (
        <IsLogin
          onSuccess={login}
          onClose={() => {
            setShowIpCheck(false);
          }}
        />
      )}
      {/* 行为验证组件 */}
      {showSecurityComponent && (
        <SecurityComponent
          // scene={SCENES.SUB_ACCOUNT_LOGIN}
          risk_token={showSecurityComponent}
          onSuccess={(risk_token) => {
            setShowSecurityComponent('');
            SecurityComponentSuccess(undefined, risk_token);
          }}
          onClose={() => {
            if (riskTokenType === 'guide') {
              loginSuccess(userInfo);
            }
            setShowSecurityComponent('');
          }}
        />
      )}
      <Google2faModal
        visible={show2faModal}
        setVisible={setShow2faModal}
        onVerifyAccount={(code) => {
          SecurityComponentSuccess(undefined, undefined, code);
        }}
        setBizToken={setBizToken}
      />
    </>
  );
};

export default LoginForm;
